/**
 * userCalendars.repository — 내 캘린더 묶음 + 표시 설정 (domain/calendar.ts, 2026-10-29).
 *
 *   users/{uid}/calendars/{autoId}      { name, color, order, created_at, updated_at }   본인 읽기·쓰기
 *   users/{uid}/calendar_prefs/state    { hidden: string[], updated_at }                 본인 읽기·쓰기
 *
 * - 문서 ID = 자동 ID. 기본 캘린더 "default" 는 문서가 없다(domain DEFAULT_CALENDAR).
 * - 캘린더 삭제 = 그 캘린더의 개인 일정을 기본으로 옮긴 뒤(calendar_id 제거, 배치) 문서 삭제. 일정은 지우지 않는다.
 * - 실시간 목록은 orderBy 를 쓰지 않는다 → 정렬은 domain sortCalendars.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { coerceHidden, coerceUserCalendar, type CalendarColorKey, type UserCalendar } from "@/domain/calendar";

type ErrCb = (e: Error & { code?: string }) => void;
const col = (uid: string) => collection(db, "users", uid, "calendars");
const prefsRef = (uid: string) => doc(db, "users", uid, "calendar_prefs", "state");

export const userCalendarsRepo = {
  watch(uid: string, cb: (list: UserCalendar[]) => void, onError?: ErrCb): () => void {
    return onSnapshot(
      col(uid),
      (snap) => cb(snap.docs.map((d) => coerceUserCalendar(d.id, d.data())).filter((c): c is UserCalendar => c !== null)),
      onError,
    );
  },

  async add(uid: string, name: string, color: CalendarColorKey, order: number): Promise<string> {
    const ref = await addDoc(col(uid), { name, color, order, created_at: serverTimestamp(), updated_at: serverTimestamp() });
    return ref.id;
  },

  async update(uid: string, id: string, patch: { name?: string; color?: CalendarColorKey; order?: number }): Promise<void> {
    await updateDoc(doc(db, "users", uid, "calendars", id), { ...patch, updated_at: serverTimestamp() });
  },

  /** 삭제 — 속한 일정은 기본 캘린더로(calendar_id 제거). 돌려주는 값 = 옮긴 일정 수 */
  async remove(uid: string, id: string): Promise<number> {
    const evs = await getDocs(query(collection(db, "users", uid, "personal_events"), where("calendar_id", "==", id)));
    let moved = 0;
    for (let i = 0; i < evs.docs.length; i += 400) {
      const b = writeBatch(db);
      for (const d of evs.docs.slice(i, i + 400)) {
        b.update(d.ref, { calendar_id: deleteField(), updated_at: serverTimestamp() });
        moved++;
      }
      await b.commit();
    }
    await deleteDoc(doc(db, "users", uid, "calendars", id));
    return moved;
  },

  watchHidden(uid: string, cb: (hidden: Set<string>) => void, onError?: ErrCb): () => void {
    return onSnapshot(prefsRef(uid), (snap) => cb(coerceHidden(snap.data())), onError);
  },

  async setHidden(uid: string, hidden: Set<string>): Promise<void> {
    await setDoc(prefsRef(uid), { hidden: [...hidden].sort(), updated_at: serverTimestamp() }, { merge: true });
  },
};
